/**
 * Procedural cathedral impulse response.
 *
 * No samples, no network: the room is synthesised. About twenty early reflections in the
 * first 80 ms (decaying, sign-randomised, L/R decorrelated, each softened by a short
 * raised-cosine kernel so stone reads as stone rather than glass), then a late field of
 * Gaussian noise whose decay is frequency dependent — built from four band layers that
 * sum back to the original noise when their decays are equal, so the crossovers introduce
 * no colour of their own. Default RT60 ≈ 7.5 s below 350 Hz, ≈ 5.2 s in the low mids, ≈ 3.4 s
 * from 1.4–4 kHz and ≈ 1.4 s above, plus air absorption: a lowpass on the whole late field
 * whose cutoff falls from 9 kHz toward 1.4 kHz over the first seconds. The late field fades
 * in over ~60 ms so the attack of a strike stays articulate.
 *
 * The core is pure (no Web Audio) so it can be exercised headless; the two exported
 * wrappers put the result in an AudioBuffer, synchronously or in chunks between tasks.
 */
import { mulberry32 } from '../util/math';

export interface CathedralOptions {
  seed?: number;
  /** RT60 seconds per band: below 350 Hz, 350 Hz–1.4 kHz, 1.4–4 kHz, above 4 kHz */
  rt60?: readonly [number, number, number, number];
  /** relative level per band (the late field's spectral tilt) */
  bandWeight?: readonly [number, number, number, number];
  /** number of early reflections */
  earlyCount?: number;
}

const BAND_EDGES_HZ = [350, 1400, 4000] as const;
const DEFAULT_RT60 = [7.5, 5.2, 3.4, 1.4] as const;
/** per-band level of the independent white sources; ≈ pink-with-a-lift in the mids */
const DEFAULT_WEIGHT = [1.0, 0.6, 0.32, 0.16] as const;
/** Butterworth order of each band edge (36 dB/oct at 6) */
const BUTTER_ORDER = 6;
/** keep the long low band out of the sub-bass */
const BAND0_HIGHPASS_HZ = 60;
/** air absorption: late-field lowpass cutoff starts here and falls exponentially to the floor */
const AIR_LOWPASS_HZ = 9000;
const AIR_LOWPASS_FLOOR_HZ = 1400;
const AIR_LOWPASS_TAU_S = 2.5;
const LATE_START_S = 0.012;
const LATE_FADE_S = 0.06;
const EARLY_FIRST_S = 0.0025;
const EARLY_WINDOW_S = 0.08;
/** early-reflection amplitude relative to the late field's unit-variance noise */
const EARLY_LEVEL = 3.5;
const EARLY_DECAY_S = 0.04;
const LN1000 = Math.log(1000);

/** Q of each second-order section of a Butterworth filter of the given (even) order. */
export function butterworthQs(order: number): number[] {
  const n = Math.max(1, Math.round(order / 2));
  const qs: number[] = [];
  for (let k = 0; k < n; k++) qs.push(1 / (2 * Math.sin(((2 * k + 1) * Math.PI) / (4 * n))));
  return qs;
}

/** RBJ cookbook biquad, normalised: [b0, b1, b2, a1, a2]. */
export function rbjBiquad(kind: 'lp' | 'hp', fc: number, q: number, sampleRate: number): [number, number, number, number, number] {
  const w = (2 * Math.PI * Math.min(fc, sampleRate * 0.45)) / sampleRate;
  const cs = Math.cos(w);
  const alpha = Math.sin(w) / (2 * q);
  const a0 = 1 + alpha;
  if (kind === 'lp') {
    const b1 = (1 - cs) / a0;
    return [b1 / 2, b1, b1 / 2, (-2 * cs) / a0, (1 - alpha) / a0];
  }
  const b1 = -(1 + cs) / a0;
  return [-b1 / 2, b1, -b1 / 2, (-2 * cs) / a0, (1 - alpha) / a0];
}

export interface CathedralRenderer {
  readonly sampleRate: number;
  readonly length: number;
  readonly left: Float32Array<ArrayBuffer>;
  readonly right: Float32Array<ArrayBuffer>;
  /** render late-field samples [from, to); call in increasing, contiguous ranges */
  fill(from: number, to: number): void;
  /** normalise (per-channel energy 1) once everything is filled */
  finish(): void;
}

export function createCathedralRenderer(sampleRate: number, seconds: number, opts: CathedralOptions = {}): CathedralRenderer {
  const sr = sampleRate;
  const N = Math.max(1024, Math.round(seconds * sr));
  const left = new Float32Array(N);
  const right = new Float32Array(N);
  const rng = mulberry32(opts.seed ?? 0x0c47);
  const rt60 = opts.rt60 ?? DEFAULT_RT60;
  const weight = opts.bandWeight ?? DEFAULT_WEIGHT;

  // ---- early reflections --------------------------------------------------
  const kLen = Math.max(3, Math.round(7 * sr / 48000) | 1);
  const kernel = new Float64Array(kLen);
  for (let i = 0; i < kLen; i++) kernel[i] = 0.5 - 0.5 * Math.cos((2 * Math.PI * (i + 1)) / (kLen + 1));
  const addTap = (buf: Float32Array, t: number, a: number) => {
    const c = Math.round(t * sr) - (kLen >> 1);
    for (let i = 0; i < kLen; i++) {
      const n = c + i;
      if (n >= 0 && n < N) buf[n] += a * kernel[i];
    }
  };
  const count = opts.earlyCount ?? 20;
  for (let k = 0; k < count; k++) {
    const u = (k + 0.25 + 0.5 * rng()) / count;
    const t = EARLY_FIRST_S + (EARLY_WINDOW_S - EARLY_FIRST_S) * Math.pow(u, 1.4);
    const a = EARLY_LEVEL * Math.exp(-t / EARLY_DECAY_S) * (0.6 + 0.4 * rng());
    const sign = rng() < 0.5 ? -1 : 1;
    // decorrelate: each ear gets its own small delay and level offset
    const spread = k < 2 ? 0.0004 : 0.0014;
    addTap(left, t + (rng() - 0.5) * spread, sign * a * (0.75 + 0.5 * rng()));
    addTap(right, t + (rng() - 0.5) * spread, sign * a * (0.75 + 0.5 * rng()));
  }

  // ---- late field ---------------------------------------------------------
  // Four bands of independent noise, each through Butterworth band edges (BUTTER_ORDER,
  // 36 dB/oct at 6) so a fast-decaying top band is not buried by leakage from the slow
  // low bands. Independent sources mean the bands add in power with no phase ripple.
  const onepole = (hz: number) => 1 - Math.exp(-2 * Math.PI * Math.min(hz, sr * 0.45) / sr);
  const stages: number[] = []; // flat biquad coefficients b0 b1 b2 a1 a2
  const bandStage: [number, number][] = [];
  const pushBand = (hp: number | null, lp: number | null) => {
    const start = stages.length / 5;
    if (hp !== null) for (const q of butterworthQs(hp === BAND0_HIGHPASS_HZ ? 2 : BUTTER_ORDER)) stages.push(...rbjBiquad('hp', hp, q, sr));
    if (lp !== null) for (const q of butterworthQs(BUTTER_ORDER)) stages.push(...rbjBiquad('lp', lp, q, sr));
    bandStage.push([start, stages.length / 5]);
  };
  pushBand(BAND0_HIGHPASS_HZ, BAND_EDGES_HZ[0]);
  pushBand(BAND_EDGES_HZ[0], BAND_EDGES_HZ[1]);
  pushBand(BAND_EDGES_HZ[1], BAND_EDGES_HZ[2]);
  pushBand(BAND_EDGES_HZ[2], null);
  const coef = Float64Array.from(stages);
  const nStages = coef.length / 5;
  // biquad state (2 per stage per channel) + air-absorption lowpass (1 per channel)
  const zs = new Float64Array(2 * nStages * 2);
  const air = new Float64Array(2);
  const decay = rt60.map((t) => Math.exp(-LN1000 / (Math.max(0.05, t) * sr)));
  let cursor = 0;
  const fadeStart = Math.round(LATE_START_S * sr);
  const fadeLen = Math.max(1, Math.round(LATE_FADE_S * sr));
  const TRI = Math.sqrt(6); // unit-variance scale for (u1 + u2 - 1)

  function fill(from: number, to: number) {
    if (from !== cursor) throw new Error('CathedralRenderer.fill: ranges must be contiguous');
    to = Math.min(to, N);
    // per-band envelopes at `from`
    const env = [0, 0, 0, 0];
    for (let b = 0; b < 4; b++) env[b] = Math.pow(decay[b], from);
    let aAir = 0;
    for (let n = from; n < to; n++) {
      // air absorption: a lowpass whose cutoff falls with time
      if ((n & 31) === 0 || n === from) {
        const t = n / sr;
        const fc = Math.max(AIR_LOWPASS_FLOOR_HZ, AIR_LOWPASS_HZ * Math.exp(-t / AIR_LOWPASS_TAU_S));
        aAir = onepole(fc);
      }
      // onset window
      let fade = 1;
      if (n < fadeStart) fade = 0;
      else if (n < fadeStart + fadeLen) fade = 0.5 - 0.5 * Math.cos(Math.PI * (n - fadeStart) / fadeLen);

      for (let ch = 0; ch < 2; ch++) {
        let sum = 0;
        for (let b = 0; b < 4; b++) {
          // triangular noise; six-pole filtering makes it Gaussian enough
          let y = (rng() + rng() - 1) * TRI;
          const [s0, s1] = bandStage[b];
          for (let s = s0; s < s1; s++) {
            const c = s * 5;
            const z = (ch * nStages + s) * 2;
            const out = coef[c] * y + zs[z];
            zs[z] = coef[c + 1] * y - coef[c + 3] * out + zs[z + 1];
            zs[z + 1] = coef[c + 2] * y - coef[c + 4] * out;
            y = out;
          }
          sum += weight[b] * env[b] * y;
        }
        air[ch] += (sum - air[ch]) * aAir;
        if (fade > 0) (ch === 0 ? left : right)[n] += air[ch] * fade;
      }
      env[0] *= decay[0]; env[1] *= decay[1]; env[2] *= decay[2]; env[3] *= decay[3];
    }
    cursor = to;
  }

  function finish() {
    if (cursor < N) fill(cursor, N);
    let e = 0;
    for (let n = 0; n < N; n++) e += left[n] * left[n] + right[n] * right[n];
    const scale = e > 0 ? 1 / Math.sqrt(e / 2) : 1;
    for (let n = 0; n < N; n++) { left[n] *= scale; right[n] *= scale; }
  }

  return { sampleRate: sr, length: N, left, right, fill, finish };
}

/** Render the whole IR synchronously (headless tests, or when a stall is acceptable). */
export function renderCathedralIR(sampleRate: number, seconds: number, opts?: CathedralOptions): CathedralRenderer {
  const r = createCathedralRenderer(sampleRate, seconds, opts);
  r.fill(0, r.length);
  r.finish();
  return r;
}

function toBuffer(ctx: BaseAudioContext, r: CathedralRenderer): AudioBuffer {
  const buf = ctx.createBuffer(2, r.length, r.sampleRate);
  buf.copyToChannel(r.left, 0);
  buf.copyToChannel(r.right, 1);
  return buf;
}

/** Synchronous: a stereo AudioBuffer of `seconds` at the context's sample rate. */
export function generateCathedralIR(ctx: BaseAudioContext, seconds: number, opts?: CathedralOptions): AudioBuffer {
  return toBuffer(ctx, renderCathedralIR(ctx.sampleRate, seconds, opts));
}

/** Chunked: yields to the event loop between ~chunkSeconds of rendered IR. */
export async function generateCathedralIRAsync(ctx: BaseAudioContext, seconds: number, opts?: CathedralOptions, chunkSeconds = 0.75): Promise<AudioBuffer> {
  const r = createCathedralRenderer(ctx.sampleRate, seconds, opts);
  const step = Math.max(4096, Math.round(chunkSeconds * ctx.sampleRate));
  for (let from = 0; from < r.length; from += step) {
    r.fill(from, Math.min(r.length, from + step));
    if (from + step < r.length) await new Promise<void>((res) => setTimeout(res, 0));
  }
  r.finish();
  return toBuffer(ctx, r);
}
